<template>
  <div class="fab-container" v-if="supportOptionsCount > 0">
    <!-- Main FAB Button -->
    <b-button
      v-b-popover.hover.top.html="popoverContent"
      :id="buttonId"
      variant="primary"
      class="fab-button"
      :class="{ 'fab-button--expanded': isHovered }"
      pill
      :aria-label="$t('support.help')"
      data-testid="fab-button"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <font-awesome-icon :icon="['fas', 'question-circle']" class="fab-icon" />
      <transition name="fade-slide">
        <span v-show="isHovered" class="fab-text">{{
          $t("support.support")
        }}</span>
      </transition>
    </b-button>

    <!-- Popover with Support Options -->
    <b-popover
      :target="buttonId"
      triggers="click"
      placement="top"
      custom-class="fab-popover"
      @show="onPopoverShow"
      @hide="onPopoverHide"
    >
      <template #title>
        <div class="popover-header-content">
          <div class="header-icon-container">
            <span>{{ $t("support.hello-there") }}</span>
            <WavingHandIcon class="header-icon" />
          </div>
          <span class="support-title">{{ $t("support.title") }}</span>
          <button
            class="close-button"
            @click="closePopover"
            aria-label="Close"
            data-testid="close-popover"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
      </template>

      <div
        class="support-options"
        :class="{ 'single-column': supportOptionsCount === 1 }"
      >
        <!-- Dynamic Support Options -->
        <a
          v-for="option in supportOptions"
          :key="option.key"
          :href="getLink(option.link)"
          :target="option.target || '_blank'"
          :rel="option.target ?? '_self' ? null : 'noopener noreferrer'"
          class="support-option"
          :data-testid="`${option.key}-support`"
          @click="trackSupportClick(option.key)"
        >
          <component :is="option.icon" class="option-icon" />
          <span class="option-title">{{ $t(option.translation) }}</span>
        </a>
      </div>
    </b-popover>
  </div>
</template>

<script>
import TelegramIcon from "@/components/icons/TelegramIcon.vue";
import WavingHandIcon from "@/components/icons/WavingHandIcon.vue";
import MailIcon from "@/components/icons/MailIcon.vue";

const SUPPORT_OPTION_DEFS = [
  {
    key: "telegram",
    icon: "TelegramIcon",
    link: "telegramLink",
    translation: "support.telegram",
    target: "_blank",
  },
  {
    key: "email",
    icon: "MailIcon",
    link: "supportEmail",
    translation: "support.email",
    target: "_self",
  },
];

export default {
  name: "FloatingActionButton",
  components: {
    TelegramIcon,
    WavingHandIcon,
    MailIcon,
  },
  data() {
    return {
      buttonId: "fab-support-button",
      isPopoverVisible: false,
      isHovered: false,
    };
  },
  computed: {
    telegramLink() {
      return process.env.VUE_APP_TELEGRAM_URL || "";
    },
    supportEmail() {
      const email = process.env.VUE_APP_SUPPORT_EMAIL;
      return email ? `mailto:${email}` : "";
    },
    supportOptions() {
      return SUPPORT_OPTION_DEFS.filter((option) => this[option.link]).map(
        (option) => ({ ...option }),
      );
    },
    supportOptionsCount() {
      return this.supportOptions.length;
    },
    popoverContent() {
      return ""; // Empty as we use template slot
    },
  },
  mounted() {
    // Add ESC key listener to close popover
    document.addEventListener("keydown", this.handleEscKey);
  },
  beforeDestroy() {
    // Clean up event listener
    document.removeEventListener("keydown", this.handleEscKey);
  },
  methods: {
    getLink(linkName) {
      // Get link from computed property by name
      return this[linkName];
    },
    handleEscKey(event) {
      if (event.key === "Escape" && this.isPopoverVisible) {
        this.$root.$emit("bv::hide::popover", this.buttonId);
      }
    },
    onPopoverShow() {
      this.isPopoverVisible = true;
    },
    onPopoverHide() {
      this.isPopoverVisible = false;
    },
    closePopover() {
      this.$root.$emit("bv::hide::popover", this.buttonId);
    },
    trackSupportClick(type) {
      // Track support option clicks for analytics
      if (this.$gtag) {
        this.$gtag.event("support_contact", {
          event_category: "Support",
          event_label: type,
        });
      }
    },
  },
};
</script>

<style lang="scss">
.fab-container {
  display: block;
  position: fixed;
  bottom: 50px;
  right: 50px;
  z-index: 999;

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
  }

  @media (max-width: 425px) {
    bottom: 30px;
    right: 30px;
  }
}

.fab-button {
  width: 60px;
  height: 60px;
  border-radius: 30px;
  background: linear-gradient(135deg, #07b53b 0%, #05903f 100%);
  border: none;
  box-shadow: 0 4px 20px rgba(7, 181, 59, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;

  &--expanded {
    width: auto;
    min-width: 150px;
    border-radius: 30px;
    gap: 12px;
    justify-content: flex-start;
    padding: 0 24px 0 20px;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 25px rgba(7, 181, 59, 0.6);
    background: linear-gradient(135deg, #06c945 0%, #07b53b 100%);
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px rgba(7, 181, 59, 0.3);
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;

    &--expanded {
      min-width: 140px;
    }
  }
}

.fab-icon {
  font-size: 24px;
  color: white;
  flex-shrink: 0;
  transition: margin 0.3s ease;

  @media (max-width: 768px) {
    font-size: 22px;
  }
}

.fab-text {
  color: white;
  font-weight: 600;
  font-size: 20px;
  letter-spacing: 0.5px;
  display: inline-block;
  margin-left: 12px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
}

// Transition animations for text
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.fade-slide-enter-to,
.fade-slide-leave {
  opacity: 1;
  transform: translateX(0);
}

// Popover Styling
.fab-popover {
  max-width: 600px !important;
  width: auto !important;
  min-width: 320px !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  @media (max-width: 425px) {
    border: none !important;
  }

  .popover-body {
    padding: 0 !important;
    background-color: #f8f8f9 !important;
    border-bottom-left-radius: 16px !important;
    border-bottom-right-radius: 16px !important;
  }

  .popover-header {
    background: #07b53b !important;
    background-image: none !important;
    color: white !important;
    border: none !important;
    padding: 16px 20px !important;
    border-top-left-radius: 16px !important;
    border-top-right-radius: 16px !important;
  }

  .arrow::after {
    border-top-color: #07b53b !important;
  }
}

.popover-header-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-weight: 600;
  font-size: 16px;
  position: relative;

  .header-icon {
    font-size: 18px;
  }

  .header-icon-container {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 16px;
    font-weight: 600;
  }

  .support-title {
    font-size: 26px;
    font-weight: 600;
  }

  .close-button {
    position: absolute;
    top: 0;
    right: 0;
    background: transparent;
    border: none;
    color: white;
    font-size: 20px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: rotate(90deg);
    }

    &:active {
      transform: rotate(90deg) scale(0.95);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
    }
  }
}

.support-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 24px;

  &.single-column {
    grid-template-columns: 1fr;
    padding: 24px;
    // Remove max-width and margin to let it fill the body
  }
}

.support-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 16px;
  border-radius: 16px;
  text-decoration: none;
  background-color: white;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  min-height: 120px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(7, 181, 59, 0.15);
    background-color: #9ce0b3;
  }

  &:active {
    transform: translateY(-2px);
  }
}

.option-icon {
  font-size: 32px;
  color: #07b53b;
  transition: all 0.3s ease;
  width: 50px;
  height: 50px;

  .support-option:hover & {
    transform: scale(1.1);
  }
}

.option-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-align: center;
  line-height: 1.4;
}

// Responsive adjustments for tablets and medium screens
@media (min-width: 426px) and (max-width: 576px) {
  .fab-popover {
    width: 340px !important;
  }

  .support-options {
    gap: 12px;
    padding: 16px;
  }

  .support-option {
    padding: 20px 12px;
    min-height: 100px;
  }

  .option-icon {
    font-size: 28px;
  }

  .option-title {
    font-size: 13px;
  }
}

// Full width on very small screens (including 320px)
@media (max-width: 425px) {
  .fab-button {
    &--expanded {
      width: 60px !important;
      min-width: 60px !important;
      gap: 0 !important;
      padding: 0 20px !important;
    }
  }

  .fab-text {
    display: none !important;
  }

  .fab-popover {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    top: auto !important;
    width: 100vw !important;
    max-width: 100vw !important;
    min-width: 100vw !important;
    margin: 0 !important;
    border-radius: 0 !important;
    transform: none !important;

    &.bs-popover-top,
    &.bs-popover-auto[x-placement^="top"] {
      .arrow {
        display: none !important;
      }
    }

    .popover-header {
      border-top-left-radius: 16px !important;
      border-top-right-radius: 16px !important;
      padding: 16px !important;
    }

    .popover-body {
      border-bottom-left-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      padding-bottom: 80px !important; // Space for FAB
    }
  }

  .support-options {
    padding: 16px !important;
    gap: 12px !important;

    &.single-column {
      padding: 16px !important;
    }
  }

  .support-option {
    padding: 20px 16px !important;
    min-height: 100px !important;
  }

  .option-icon {
    width: 48px !important;
    height: 48px !important;
  }

  .option-title {
    font-size: 14px !important;
  }

  .support-title {
    font-size: 20px !important;
  }

  .header-icon-container {
    font-size: 14px !important;
  }
}

// Animation for popover entrance
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

::v-deep .fab-popover.show {
  animation: fadeInUp 0.3s ease;
}
</style>
